/**
 * Document Approval Component
 *
 * Admin UI for approving/rejecting pending institutional documents.
 * Shows documents awaiting approval in a table with action buttons.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useAuthStore } from '@/stores/auth';
import { adminApi } from '@/api/endpoints/admin';
import type { DocumentListResult } from '@/api/endpoints/documents';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/documents/StatusBadge';
import { cn } from '@/lib/utils';
import { createLogger } from '@/utils/logger';
import { sanitizeErrorMessage } from '@/utils/errorMessages';
import { formatDayStamp } from '@/utils/dates';

const logger = createLogger('DocumentApproval');

export function DocumentApproval() {
  const { t } = useTranslation();
  // Mirrors the formatCategory helper in DocumentList.tsx — i18n keyed off
  // documents.list.category.<token>, with the title-cased category string
  // as the fallback when no translation exists.
  const formatCategory = (category: string): string => {
    if (!category) return t('documents.list.category.unknown');
    const key = `documents.list.category.${category}`;
    const translated = t(key);
    return translated === key ? category.charAt(0).toUpperCase() + category.slice(1) : translated;
  };
  // useAuthenticatedQuery inherits the QueryClient's retry budget (retry:2,
  // no-retry-on-4xx) so a cold-start 503 stays in the skeleton state through
  // the api client's retry instead of flashing an error; refetch backs both
  // the user-initiated Retry action and the post-mutation list refresh.
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useAuthenticatedQuery<DocumentListResult, ApiError>({
    queryKey: ['admin', 'pending-documents'],
    queryFn: () => adminApi.listPendingDocuments(),
    staleTime: 60_000,
  });

  const documents = data?.documents ?? [];
  const total = data?.totalCount ?? 0;

  // Mutation (approve/reject) failures are distinct from a list-load failure —
  // surfaced inline so the user knows the action didn't take, without wiping
  // the table they're acting on.
  const [actionError, setActionError] = useState<string | null>(null);
  const meId = useAuthStore((s) => s.user?.id);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  const handleApprove = async (documentId: string) => {
    if (actionInProgress) return;
    // The sign-off's URI names its approver, and the server refuses a row that
    // is not the caller's own — so without an identity there is nothing to PUT.
    if (!meId) {
      setActionError('Cannot record an approval without a signed-in approver.');
      return;
    }
    setActionInProgress(documentId);
    setActionError(null);

    try {
      await adminApi.putDocumentApproval(documentId, meId);
      logger.info('Document approved', { documentId });
      // Refresh list
      await refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to approve document';
      logger.error('Approval failed', { documentId, error: msg });
      setActionError(sanitizeErrorMessage(msg));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = (documentId: string) => {
    if (actionInProgress) return;
    setRejectingId(documentId);
  };

  const confirmReject = async (documentId: string) => {
    if (actionInProgress) return;
    setActionInProgress(documentId);
    setRejectingId(null);
    setActionError(null);

    try {
      await adminApi.deleteDocument(documentId);
      logger.info('Document rejected', { documentId });
      // Refresh list
      await refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to reject document';
      logger.error('Rejection failed', { documentId, error: msg });
      setActionError(sanitizeErrorMessage(msg));
    } finally {
      setActionInProgress(null);
    }
  };

  const cancelReject = () => {
    setRejectingId(null);
  };

  if (isError) {
    // The knowledge-service being unconfigured in an environment is an
    // expected, non-alarming state (amber, no action). A genuine load
    // failure is red and carries a Retry action. The backend emits exactly
    // SERVICE_UNAVAILABLE for the not-configured case
    // (GET /documents?status=); a downstream gRPC failure maps to
    // INTERNAL_ERROR, so the structured code alone is the precise signal —
    // no message-substring matching.
    const isUnavailable = error?.code === 'SERVICE_UNAVAILABLE';
    return (
      <div className={cn(
        'rounded-xl border p-4 text-sm',
        isUnavailable
          ? 'bg-amber-50 border-amber-200 text-amber-800'
          : 'bg-red-50 border-red-200 text-red-700',
      )}>
        {isUnavailable ? (
          <>
            <p className="font-medium">
              {t('admin.documentApproval.serviceUnavailable', 'Knowledge service is not available in this environment.')}
            </p>
            <p className="mt-1 text-xs text-amber-600">
              {t('admin.documentApproval.serviceUnavailableHint', 'The knowledge-service must be running for document approval.')}
            </p>
          </>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <p className="font-medium">{t('admin.documentApproval.loadError', 'Failed to load pending documents')}</p>
            <Button
              onClick={() => void refetch()}
              disabled={isRefetching}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
              {isRefetching ? t('actions.retrying') : t('actions.retry')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="space-y-4">
        {actionError && <ActionErrorBanner message={actionError} />}
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <p className="text-neutral-500">{t('admin.documentApproval.empty', 'No documents pending approval')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {actionError && <ActionErrorBanner message={actionError} />}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.documentApproval.columnTitle', 'Title')}</TableHead>
              <TableHead>{t('admin.documentApproval.columnCategory', 'Category')}</TableHead>
              <TableHead>{t('admin.documentApproval.columnStatus', 'Status')}</TableHead>
              <TableHead>{t('admin.documentApproval.columnUploaded', 'Uploaded')}</TableHead>
              <TableHead>{t('admin.documentApproval.columnChunks', 'Chunks')}</TableHead>
              <TableHead className="text-right">{t('admin.documentApproval.columnActions', 'Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell className="font-medium max-w-xs truncate" title={doc.title}>
                  {doc.title}
                </TableCell>
                <TableCell>{formatCategory(doc.category)}</TableCell>
                <TableCell>
                  <StatusBadge status={doc.status} />
                </TableCell>
                <TableCell className="text-neutral-500 text-sm">
                  {formatDayStamp(doc.created_at, t)}
                </TableCell>
                <TableCell className="text-neutral-500 text-sm">
                  {doc.chunk_count > 0 ? doc.chunk_count : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {rejectingId === doc.id ? (
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-neutral-500 mr-2">{t('admin.documentApproval.deleteConfirmPrompt', 'Delete permanently?')}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => confirmReject(doc.id)}
                        disabled={actionInProgress === doc.id}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        {t('admin.documentApproval.confirm', 'Confirm')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelReject}
                        disabled={actionInProgress === doc.id}
                        className="text-neutral-600 border-neutral-200 hover:bg-neutral-50"
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleApprove(doc.id)}
                        disabled={actionInProgress === doc.id || rejectingId !== null}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        {actionInProgress === doc.id ? t('admin.documentApproval.approving', 'Approving...') : t('admin.documentApproval.approve', 'Approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(doc.id)}
                        disabled={actionInProgress === doc.id || rejectingId !== null}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        {t('admin.documentApproval.reject', 'Reject')}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {total > documents.length && (
        <p className="text-xs text-neutral-400 text-center">
          {t('admin.documentApproval.showingDocuments', { shown: documents.length, total, defaultValue: 'Showing {{shown}} of {{total}} documents' })}
        </p>
      )}
    </div>
  );
}

/** Inline notice for a failed approve/reject action (distinct from a
 *  list-load failure — the table stays visible so the user can retry the
 *  action on the row). */
function ActionErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      {message}
    </div>
  );
}
